/**
 * Utility functions for determining fallback room avatars based on member lists
 * 
 * This module provides pure logic for selecting which members should be displayed
 * as fallback avatars when a room has no explicit avatar set.
 * 
 * Logic:
 * - 1 member (you only): Your avatar
 * - 2 members (you + 1 other): The other person's avatar
 * - 3+ members: First two other members' avatars (excluding you)
 */

/**
 * Determines which members should be used for fallback avatar display
 * 
 * @param {Array<Object>} members - Array of member objects with userId property
 * @param {Function} isCurrentUser - Function that returns true if userId is current user
 * @returns {Array<Object>} Array of members to use for fallback avatars (0-2 members)
 * 
 * @example
 * // For a room with 3 members: [@me:server, @alice:server, @bob:server]
 * const members = room.getJoinedMembers();
 * const fallbackMembers = selectFallbackMembers(members, (id) => id === client.getUserId());
 * // Returns: [@alice:server, @bob:server]
 * 
 * @example
 * // For room creation with selected users (not yet joined)
 * const selectedUsers = [{userId: '@alice:server', name: 'Alice'}, {userId: '@bob:server', name: 'Bob'}];
 * const currentUserId = '@me:server';
 * const fallbackMembers = selectFallbackMembers(selectedUsers, (id) => id === currentUserId);
 * // Returns: [@alice:server, @bob:server]
 */
export function selectFallbackMembers(members, isCurrentUser) {
  // Validate inputs
  if (!Array.isArray(members)) {
    return [];
  }
  
  if (typeof isCurrentUser !== 'function') {
    throw new Error('isCurrentUser must be a function');
  }

  // Filter out any invalid members
  const validMembers = members.filter(m => m && m.userId);

  if (validMembers.length === 0) {
    return [];
  }

  switch (validMembers.length) {
    case 1:
      // Single participant (you only) - use your avatar
      return [validMembers[0]];

    case 2: {
      // Two participants (you + 1 other) - return only the other person
      const otherUser = validMembers.find(member => !isCurrentUser(member.userId));
      // If both members are not the current user (e.g. a DM between two others),
      // or if for some reason the other user isn't found, just return the first one.
      // This ensures we always show one avatar for a 1:1 chat.
      return otherUser ? [otherUser] : [validMembers[0]];
    }

    default: {
      // Multiple participants - use first two other users (excluding current user)
      const otherUsers = validMembers.filter(member => !isCurrentUser(member.userId));
      return otherUsers.slice(0, 2);
    }
  }
}

/**
 * Determines how many avatar slots should be displayed
 * 
 * @param {Array<Object>} members - Array of member objects
 * @param {Function} isCurrentUser - Function that returns true if userId is current user
 * @returns {number} Number of avatar slots (0, 1, or 2)
 */
export function getAvatarSlotCount(members, isCurrentUser) {
  const fallbackMembers = selectFallbackMembers(members, isCurrentUser);
  return fallbackMembers.length;
}

/**
 * Checks if fallback avatars should be displayed as dual avatars
 * 
 * @param {Array<Object>} members - Array of member objects
 * @param {Function} isCurrentUser - Function that returns true if userId is current user
 * @returns {boolean} True if dual avatars should be shown
 */
export function shouldShowDualAvatars(members, isCurrentUser) {
  const fallbackMembers = selectFallbackMembers(members, isCurrentUser);
  return fallbackMembers.length === 2;
}

/**
 * Gets display information for fallback avatars
 * Includes user ID, display name, and whether it's the current user
 * 
 * @param {Array<Object>} members - Array of member objects with userId and optional name/rawDisplayName
 * @param {Function} isCurrentUser - Function that returns true if userId is current user
 * @returns {Array<Object>} Array of display info objects
 * 
 * @example
 * const displayInfo = getFallbackAvatarDisplayInfo(members, (id) => id === myUserId);
 * // Returns: [
 * //   { userId: '@alice:server', displayName: 'Alice', isCurrentUser: false },
 * //   { userId: '@bob:server', displayName: 'Bob', isCurrentUser: false }
 * // ]
 */
export function getFallbackAvatarDisplayInfo(members, isCurrentUser) {
  const fallbackMembers = selectFallbackMembers(members, isCurrentUser);
  
  return fallbackMembers.map(member => ({
    userId: member.userId,
    displayName: member.rawDisplayName || member.name || member.userId,
    isCurrentUser: isCurrentUser(member.userId),
  }));
}
